import React, {useEffect, useState} from 'react';

import {Accordion, Alert, Button} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import Styled from 'styled-components/macro';
import * as tracking from '../../../tracking';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {TopHeaderBar} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import type {SettingsCustomFields_viewer$data as SettingsCustomFields_viewer} from './__generated__/SettingsCustomFields_viewer.graphql';
import CustomFieldTable from './components/CustomFieldTable';
import {EntityType, EntityTypeLabels} from './Constants';
import {showCustomFieldDefinitionModal} from './modals/CustomFieldDefinitionModal';
import {AlertMessage} from './types';

const HeaderBar = Styled.div`
    display: flex;
    justify-content: space-between;
    padding: 16px 0px;
`;

const Content = Styled.div`
    max-width: 1220px;
    padding-right: 32px;
    padding-left: 32px;
`;

type SettingsCustomFieldsProps = {
	viewer: SettingsCustomFields_viewer;
};

type CustomFieldsAccordionItemProps = {
	viewer: SettingsCustomFields_viewer;
	entityType: EntityType;
	onEdit: (id: string) => void;
	onCreate: () => void;
	setAlertMessage: (message: AlertMessage) => void;
	'data-cy': string;
};

const CustomFieldsAccordionItem: React.FC<CustomFieldsAccordionItemProps> = ({
	viewer,
	entityType,
	onEdit,
	onCreate,
	setAlertMessage,
	'data-cy': dataCy,
}) => {
	return (
		<Accordion.Item key={entityType} label={EntityTypeLabels[entityType]} content="" data-cy={dataCy}>
			<CustomFieldTable
				company={viewer.company}
				entityType={entityType}
				onEditCustomField={id => onEdit(id)}
				onCreateCustomField={() => onCreate()}
				setAlertMessage={setAlertMessage}
			/>
		</Accordion.Item>
	);
};

const SettingsCustomFields = ({viewer}: SettingsCustomFieldsProps) => {
	const {formatMessage} = useIntl();
	const [alertMessage, setAlertMessage] = useState<AlertMessage | null>(null);

	useTrackPage('Custom Fields');

	useEffect(() => {
		document.title = 'Custom Fields - Account - Forecast';
		tracking.trackPage('settings-custom-fields');
	}, []);

	const editCustomFieldDefinition = (id: string) => {
		showCustomFieldDefinitionModal({
			id,
			setAlertMessage,
		});
	};

	const createCustomFieldDefinition = (entityType: EntityType) => {
		showCustomFieldDefinitionModal({
			entityType,
			setAlertMessage,
		});
	};

	return (
		<Content>
			<HeaderBar>
				<TopHeaderBar title={formatMessage({id: 'settings.custom_fields.title'})} />
				<Button
					onPress={() => {
						createCustomFieldDefinition(EntityType.PROJECT);
					}}
					data-cy={`create-custom-fields-definition`}
				>
					{formatMessage({id: 'settings.custom-fields.create'})}
				</Button>
			</HeaderBar>
			{!!alertMessage && <Alert {...alertMessage} />}
			<Accordion selectionType={Accordion.SelectionType.SINGLE} background={false} border={false}>
				{[EntityType.PROJECT, EntityType.PHASE, EntityType.TASK, EntityType.TIME_REG].map(entityType => (
					<CustomFieldsAccordionItem
						key={entityType}
						viewer={viewer}
						entityType={entityType}
						onEdit={id => editCustomFieldDefinition(id)}
						onCreate={() => createCustomFieldDefinition(entityType)}
						setAlertMessage={setAlertMessage}
						data-cy={`custom-fields-${entityType}`}
					/>
				))}
			</Accordion>
		</Content>
	);
};

export const SettingsCustomFieldsQuery = graphql`
	query SettingsCustomFieldsQuery {
		viewer {
			actualPersonId
			component(name: "settings_custom_fields")
			...SettingsCustomFields_viewer
		}
	}
`;

export default createFragmentContainer(SettingsCustomFields, {
	viewer: graphql`
		fragment SettingsCustomFields_viewer on Viewer {
			actualPersonId
			id
			company {
				...CustomFieldTable_company
			}
		}
	`,
});
